// src/pages/Admin/Fruits.jsx
// ✅ FIXED: Images now load correctly in table rows AND grid cards
// Uses getFruitImageUrl() helper — consistent with EditFruit image display

import React, { useState, useEffect } from 'react';
import {
  Container, Paper, Typography, Box, Button, Grid, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, IconButton, TextField, InputAdornment, Dialog, DialogTitle,
  DialogContent, DialogActions, Menu, MenuItem, Avatar, Switch, Tooltip,
  Alert, CircularProgress, Checkbox, Pagination, Select, InputLabel,
  FormControl,
} from '@mui/material';
import {
  Search, Add, Edit, Delete, MoreVert, Inventory,
  Category, AttachMoney, Refresh, CheckCircle, Cancel, Download,
  ClearAll, Sort, ArrowUpward, ArrowDownward, FilterAlt, FilterAltOff,
  DataArray, Flag, GridView, TableRows, ListAlt, Grading,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import { getFruitImageUrl } from '../../utils/imageUrl';   // ✅ import helper
import toast from 'react-hot-toast';

// ── Array helpers ─────────────────────────────────────────────────────────────
const parseArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [String(parsed)];
  } catch {
    return typeof value === 'string' && value.trim()
      ? value.split(',').map((s) => s.trim()).filter(Boolean)
      : [];
  }
};

const getVarieties = (f) => parseArray(f.varieties ?? f.variety);
const getGrades    = (f) => parseArray(f.grades    ?? f.grade);
const getOrigins   = (f) => parseArray(f.origins   ?? f.origin_country);

// ── Confirmation Dialog ───────────────────────────────────────────────────────
const ConfirmationDialog = ({ open, title, message, confirmText, cancelText, onConfirm, onCancel }) => (
  <Dialog open={open} onClose={onCancel} maxWidth="sm" fullWidth>
    <DialogTitle>{title}</DialogTitle>
    <DialogContent>
      <Alert severity="warning" sx={{ mb: 1 }}>{message}</Alert>
    </DialogContent>
    <DialogActions>
      <Button onClick={onCancel}>{cancelText || 'Cancel'}</Button>
      <Button onClick={onConfirm} color="error" variant="contained">{confirmText || 'Confirm'}</Button>
    </DialogActions>
  </Dialog>
);

// ── Bulk Actions Menu ─────────────────────────────────────────────────────────
const BulkActionsMenu = ({ anchorEl, open, onClose, onAction, selectedCount }) => (
  <Menu anchorEl={anchorEl} open={open} onClose={onClose}>
    <MenuItem disabled sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
      Bulk Actions ({selectedCount} selected)
    </MenuItem>
    {[
      { label: 'Activate Selected',   action: 'activate',   icon: <CheckCircle sx={{ mr: 1 }} /> },
      { label: 'Deactivate Selected', action: 'deactivate', icon: <Cancel sx={{ mr: 1 }} /> },
      { label: 'Delete Selected',     action: 'delete',     icon: <Delete sx={{ mr: 1 }} />, color: 'error.main' },
    ].map((a) => (
      <MenuItem key={a.action} sx={{ color: a.color || 'inherit' }}
        onClick={() => { onAction(a.action); onClose(); }}>
        {a.icon}{a.label}
      </MenuItem>
    ))}
  </Menu>
);

// ── ✅ FruitAvatar — loads real image, falls back to letter ──────────────────
const FruitAvatar = ({ fruit, size = 50, rounded = true }) => {
  const [imgError, setImgError] = useState(false);
  const src = getFruitImageUrl(fruit);

  if (src && !imgError) {
    return (
      <Avatar
        src={src}
        variant={rounded ? 'rounded' : 'circular'}
        sx={{ width: size, height: size }}
        onError={() => setImgError(true)}   // ← fall back to initials on 404
        imgProps={{ loading: 'lazy' }}
      />
    );
  }

  // Fallback: coloured avatar with first letter
  return (
    <Avatar
      variant={rounded ? 'rounded' : 'circular'}
      sx={{ width: size, height: size, bgcolor: 'primary.light', color: 'primary.dark', fontWeight: 'bold' }}
    >
      {(fruit?.name || 'F').charAt(0).toUpperCase()}
    </Avatar>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
const AdminFruits = () => {
  const navigate = useNavigate();

  const [fruits,           setFruits]           = useState([]);
  const [filteredFruits,   setFilteredFruits]   = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [searchTerm,       setSearchTerm]       = useState('');
  const [filter,           setFilter]           = useState('all');
  const [categoryFilter,   setCategoryFilter]   = useState('all');
  const [deleteDialog,     setDeleteDialog]     = useState(null);
  const [anchorEl,         setAnchorEl]         = useState(null);
  const [selectedFruit,    setSelectedFruit]    = useState(null);
  const [bulkSelected,     setBulkSelected]     = useState([]);
  const [bulkActionAnchor, setBulkActionAnchor] = useState(null);
  const [page,             setPage]             = useState(0);
  const [rowsPerPage,      setRowsPerPage]      = useState(10);
  const [sortField,        setSortField]        = useState('created_at');
  const [sortDirection,    setSortDirection]    = useState('desc');
  const [stats,            setStats]            = useState(null);
  const [categories,       setCategories]       = useState([]);
  const [viewMode,         setViewMode]         = useState('table');
  const [showFilters,      setShowFilters]      = useState(false);
  const [bulkActionDialog, setBulkActionDialog] = useState({ open: false, action: null });

  useEffect(() => { fetchFruits(); fetchCategories(); }, []);
  useEffect(() => { filterAndSortFruits(); },
    // eslint-disable-next-line
    [fruits, searchTerm, filter, categoryFilter, sortField, sortDirection]);

  const fetchFruits = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getAllFruits();
      const data     = response.data;
      setFruits(Array.isArray(data) ? data : (data.fruits || []));
      setStats(data.stats || null);
    } catch (error) {
      console.error('Error fetching fruits:', error);
      toast.error('Failed to load fruits');
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await adminAPI.getCategories();
      setCategories(response.data || []);
    } catch { /* non-critical */ }
  };

  const filterAndSortFruits = () => {
    let result = [...fruits];
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter((f) =>
        f.name?.toLowerCase().includes(q) ||
        f.category?.toLowerCase().includes(q) ||
        f.description?.toLowerCase().includes(q) ||
        getVarieties(f).some((v) => v.toLowerCase().includes(q))
      );
    }
    if (filter === 'active')          result = result.filter((f) => f.active);
    if (filter === 'inactive')        result = result.filter((f) => !f.active);
    if (categoryFilter !== 'all')     result = result.filter((f) => f.category === categoryFilter);

    result.sort((a, b) => {
      let av = a[sortField], bv = b[sortField];
      if (sortField === 'price_per_unit') { av = parseFloat(a.price_per_unit) || 0; bv = parseFloat(b.price_per_unit) || 0; }
      if (sortField === 'variety_count')  { av = getVarieties(a).length; bv = getVarieties(b).length; }
      if (av < bv) return sortDirection === 'asc' ? -1 :  1;
      if (av > bv) return sortDirection === 'asc' ?  1 : -1;
      return 0;
    });

    setFilteredFruits(result);
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
  };

  const handleMenuOpen  = (e, fruit) => { setAnchorEl(e.currentTarget); setSelectedFruit(fruit); };
  const handleMenuClose = ()          => { setAnchorEl(null); setSelectedFruit(null); };

  const confirmDelete = async () => {
    try {
      await adminAPI.deleteFruit(deleteDialog.id);
      toast.success('Fruit deleted successfully');
      fetchFruits();
    } catch { toast.error('Failed to delete fruit'); }
    finally  { setDeleteDialog(null); }
  };

  const handleToggleStatus = async (fruit) => {
    try {
      await adminAPI.toggleFruitActive(fruit.id);
      toast.success(`Fruit ${fruit.active ? 'deactivated' : 'activated'}`);
      fetchFruits();
    } catch { toast.error('Failed to update status'); }
  };

  const handleBulkSelect = (fruit) => {
    setBulkSelected((prev) =>
      prev.includes(fruit.id) ? prev.filter((id) => id !== fruit.id) : [...prev, fruit.id]
    );
  };

  const handleSelectAll = () => {
    setBulkSelected(
      bulkSelected.length === filteredFruits.length ? [] : filteredFruits.map((f) => f.id)
    );
  };

  const executeBulkAction = async () => {
    try {
      const res = await adminAPI.bulkUpdateFruits({ fruit_ids: bulkSelected, action: bulkActionDialog.action });
      toast.success(res.data.message);
      setBulkSelected([]);
      fetchFruits();
    } catch { toast.error('Failed to perform bulk action'); }
    finally  { setBulkActionDialog({ open: false, action: null }); }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <Sort fontSize="small" />;
    return sortDirection === 'asc'
      ? <ArrowUpward fontSize="small" />
      : <ArrowDownward fontSize="small" />;
  };

  const renderChips = (items, max = 2) => {
    const arr = Array.isArray(items) ? items : [];
    if (!arr.length) return <Typography variant="caption" color="text.secondary">—</Typography>;
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center' }}>
        {arr.slice(0, max).map((item, i) => (
          <Chip key={i} label={item} size="small" variant="outlined"
            sx={{ height: 20, fontSize: '0.7rem' }} />
        ))}
        {arr.length > max && (
          <Chip label={`+${arr.length - max}`} size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
        )}
      </Box>
    );
  };

  // ── Table Row ─────────────────────────────────────────────────────────────
  const renderTableRow = (fruit) => (
    <TableRow key={fruit.id} hover selected={bulkSelected.includes(fruit.id)}>
      <TableCell padding="checkbox">
        <Checkbox checked={bulkSelected.includes(fruit.id)}
          onChange={() => handleBulkSelect(fruit)} />
      </TableCell>

      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          {/* ✅ FruitAvatar loads real image */}
          <FruitAvatar fruit={fruit} size={50} />
          <Box>
            <Typography variant="subtitle2" fontWeight="medium">{fruit.name}</Typography>
            <Typography variant="caption" color="text.secondary">ID: {fruit.id}</Typography>
          </Box>
        </Box>
      </TableCell>

      <TableCell>
        <Chip label={fruit.category || 'Uncategorized'} size="small" variant="outlined" />
      </TableCell>

      <TableCell>
        {renderChips(getVarieties(fruit))}
        <Typography variant="caption" color="text.secondary" display="block">
          {getVarieties(fruit).length} {getVarieties(fruit).length === 1 ? 'variety' : 'varieties'}
        </Typography>
      </TableCell>

      <TableCell>{renderChips(getGrades(fruit))}</TableCell>
      <TableCell>{renderChips(getOrigins(fruit))}</TableCell>

      <TableCell>
        <Typography variant="body2" fontWeight="medium">
          ${parseFloat(fruit.price_per_unit || 0).toFixed(2)}
        </Typography>
        <Typography variant="caption" color="text.secondary">{fruit.moq}</Typography>
      </TableCell>

      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Switch size="small" checked={!!fruit.active} color="success"
            onChange={() => handleToggleStatus(fruit)} />
          <Chip label={fruit.active ? 'Active' : 'Inactive'} size="small"
            color={fruit.active ? 'success' : 'default'}
            variant={fruit.active ? 'filled' : 'outlined'} />
        </Box>
      </TableCell>

      <TableCell>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Edit">
            <IconButton size="small" color="primary"
              onClick={() => navigate(`/admin/fruits/edit/${fruit.id}`)}>
              <Edit />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => setDeleteDialog(fruit)}>
              <Delete />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={(e) => handleMenuOpen(e, fruit)}>
            <MoreVert />
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  );

  // ── Grid View ─────────────────────────────────────────────────────────────
  const renderGridView = () => (
    <Grid container spacing={3}>
      {filteredFruits
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((fruit) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={fruit.id}>
            <Card sx={{ borderRadius: 2, height: '100%', position: 'relative' }}>
              {/* ✅ Grid card image — uses FruitAvatar for consistent loading */}
              <Box sx={{ position: 'relative', bgcolor: 'grey.100', borderRadius: '8px 8px 0 0', overflow: 'hidden', height: 180 }}>
                {(() => {
                  const src = getFruitImageUrl(fruit);
                  return src ? (
                    <Box
                      component="img"
                      src={src}
                      alt={fruit.name}
                      onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
                      sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  ) : null;
                })()}
                {/* Fallback for grid if image fails */}
                <Box sx={{
                  display: getFruitImageUrl(fruit) ? 'none' : 'flex',
                  width: '100%', height: '100%',
                  alignItems: 'center', justifyContent: 'center',
                  bgcolor: 'primary.light',
                }}>
                  <Typography variant="h2" sx={{ color: 'primary.dark', fontWeight: 'bold' }}>
                    {(fruit.name || 'F').charAt(0).toUpperCase()}
                  </Typography>
                </Box>
                {/* Checkbox overlay */}
                <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
                  <Checkbox checked={bulkSelected.includes(fruit.id)}
                    onChange={() => handleBulkSelect(fruit)}
                    sx={{ bgcolor: 'rgba(255,255,255,0.85)', borderRadius: 1, p: 0.5 }} />
                </Box>
              </Box>

              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="h6" noWrap sx={{ maxWidth: '70%' }}>{fruit.name}</Typography>
                  <Chip label={fruit.active ? 'Active' : 'Inactive'} size="small"
                    color={fruit.active ? 'success' : 'default'} />
                </Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  {fruit.category}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                  {getVarieties(fruit).slice(0, 2).map((v, i) => (
                    <Chip key={i} label={v} size="small" variant="outlined" />
                  ))}
                  {getVarieties(fruit).length > 2 && (
                    <Chip label={`+${getVarieties(fruit).length - 2}`} size="small" />
                  )}
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" color="primary">
                    ${parseFloat(fruit.price_per_unit || 0).toFixed(2)}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <IconButton size="small" onClick={() => navigate(`/admin/fruits/edit/${fruit.id}`)}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => setDeleteDialog(fruit)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
    </Grid>
  );

  if (loading && !fruits.length) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography color="text.secondary" sx={{ mt: 2 }}>Loading fruits...</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>Fruits Management</Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your fruit inventory and listings
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {bulkSelected.length > 0 && (
            <Button variant="contained" color="primary" startIcon={<ListAlt />}
              onClick={(e) => setBulkActionAnchor(e.currentTarget)}>
              Bulk Actions ({bulkSelected.length})
            </Button>
          )}
          <Button variant="contained" startIcon={<Add />} size="large"
            onClick={() => navigate('/admin/fruits/add')}>
            Add New Fruit
          </Button>
        </Box>
      </Box>

      {/* Bulk menus */}
      <BulkActionsMenu anchorEl={bulkActionAnchor} open={Boolean(bulkActionAnchor)}
        onClose={() => setBulkActionAnchor(null)}
        onAction={(a) => { setBulkActionDialog({ open: true, action: a }); setBulkActionAnchor(null); }}
        selectedCount={bulkSelected.length} />

      <ConfirmationDialog open={bulkActionDialog.open} title="Confirm Bulk Action"
        message={`Are you sure you want to ${bulkActionDialog.action} ${bulkSelected.length} fruit(s)?`}
        confirmText={`Yes, ${bulkActionDialog.action}`} cancelText="Cancel"
        onConfirm={executeBulkAction}
        onCancel={() => setBulkActionDialog({ open: false, action: null })} />

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Total Fruits', value: stats?.total ?? fruits.length,
            sub: `${stats?.active ?? fruits.filter(f => f.active).length} active`,
            icon: <Inventory />, color: '#2196f3' },
          { label: 'Categories',  value: stats?.categories?.length ?? categories.length,
            sub: 'Unique categories', icon: <Category />, color: '#9c27b0' },
          { label: 'Total Value', value: `$${fruits.reduce((s, f) => s + (parseFloat(f.price_per_unit) || 0), 0).toFixed(2)}`,
            sub: 'Total inventory value', icon: <AttachMoney />, color: '#4caf50' },
          { label: 'Avg. Price',  value: `$${(fruits.reduce((s, f) => s + (parseFloat(f.price_per_unit) || 0), 0) / (fruits.length || 1)).toFixed(2)}`,
            sub: 'Average price per unit', icon: <AttachMoney />, color: '#ff9800' },
        ].map((card) => (
          <Grid item xs={12} sm={6} md={3} key={card.label}>
            <Card sx={{ borderRadius: 2 }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography color="textSecondary" variant="body2">{card.label}</Typography>
                    <Typography variant="h4" fontWeight="bold">{card.value}</Typography>
                  </Box>
                  <Avatar sx={{ bgcolor: `${card.color}20`, color: card.color }}>{card.icon}</Avatar>
                </Box>
                <Typography variant="caption" color="textSecondary">{card.sub}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Search + Filters */}
      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField fullWidth size="small"
              placeholder="Search fruits by name, category, or description..."
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search /></InputAdornment>,
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}><ClearAll /></IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end', alignItems: 'center' }}>
              <Button startIcon={showFilters ? <FilterAltOff /> : <FilterAlt />}
                onClick={() => setShowFilters(!showFilters)} variant="outlined" size="small">
                {showFilters ? 'Hide' : 'Filters'}
              </Button>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title="Table view">
                  <Button variant={viewMode === 'table' ? 'contained' : 'outlined'} size="small"
                    onClick={() => setViewMode('table')}><TableRows /></Button>
                </Tooltip>
                <Tooltip title="Grid view">
                  <Button variant={viewMode === 'grid' ? 'contained' : 'outlined'} size="small"
                    onClick={() => setViewMode('grid')}><GridView /></Button>
                </Tooltip>
              </Box>
              <Button startIcon={<Refresh />} onClick={fetchFruits} variant="outlined" size="small">
                Refresh
              </Button>
            </Box>
          </Grid>

          {showFilters && (
            <Grid item xs={12}>
              <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select value={filter} onChange={(e) => setFilter(e.target.value)} label="Status">
                        <MenuItem value="all">All Status</MenuItem>
                        <MenuItem value="active">Active</MenuItem>
                        <MenuItem value="inactive">Inactive</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Category</InputLabel>
                      <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} label="Category">
                        <MenuItem value="all">All Categories</MenuItem>
                        {categories.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <Button startIcon={<ClearAll />} variant="outlined" fullWidth
                      onClick={() => { setFilter('all'); setCategoryFilter('all'); setSearchTerm(''); }}>
                      Clear Filters
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          )}
        </Grid>
      </Paper>

      {/* Bulk selection bar */}
      {bulkSelected.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.light' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle2" sx={{ color: 'white' }}>
              {bulkSelected.length} fruit(s) selected
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button size="small" sx={{ color: 'white' }} onClick={handleSelectAll}>
                {bulkSelected.length === filteredFruits.length ? 'Deselect All' : 'Select All'}
              </Button>
              <Button size="small" sx={{ color: 'white' }} onClick={() => setBulkSelected([])}>
                Clear
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {/* Table / Grid */}
      {viewMode === 'table' ? (
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                  <TableCell padding="checkbox" width={60}>
                    <Checkbox
                      checked={bulkSelected.length === filteredFruits.length && filteredFruits.length > 0}
                      indeterminate={bulkSelected.length > 0 && bulkSelected.length < filteredFruits.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  {[
                    { label: 'Fruit',     field: 'name'          },
                    { label: 'Category',  field: 'category'      },
                    { label: 'Varieties', field: null,  extra: <DataArray fontSize="small" /> },
                    { label: 'Grades',    field: null,  extra: <Grading fontSize="small" /> },
                    { label: 'Origins',   field: null,  extra: <Flag fontSize="small" /> },
                    { label: 'Price',     field: 'price_per_unit' },
                    { label: 'Status',    field: 'active'         },
                    { label: 'Actions',   field: null             },
                  ].map((col) => (
                    <TableCell key={col.label}
                      onClick={col.field ? () => handleSort(col.field) : undefined}
                      sx={{ cursor: col.field ? 'pointer' : 'default', userSelect: 'none' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="subtitle2" fontWeight="bold">{col.label}</Typography>
                        {col.extra}
                        {col.field && getSortIcon(col.field)}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredFruits.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                      <Inventory sx={{ fontSize: 60, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="h6" color="text.secondary">No fruits found</Typography>
                      <Button variant="contained" startIcon={<Add />} sx={{ mt: 2 }}
                        onClick={() => navigate('/admin/fruits/add')}>
                        Add Your First Fruit
                      </Button>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredFruits
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((fruit) => renderTableRow(fruit))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {filteredFruits.length > 0 && (
            <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between',
                       alignItems: 'center', borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                Showing {Math.min((page + 1) * rowsPerPage, filteredFruits.length)} of {filteredFruits.length} fruits
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">Rows per page:</Typography>
                <Select size="small" value={rowsPerPage}
                  onChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}>
                  {[5, 10, 25, 50].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                </Select>
                <Pagination count={Math.ceil(filteredFruits.length / rowsPerPage)}
                  page={page + 1} onChange={(_, v) => setPage(v - 1)}
                  color="primary" size="small" />
              </Box>
            </Box>
          )}
        </Paper>
      ) : (
        <Box>
          {renderGridView()}
          {filteredFruits.length > 0 && (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
              <Pagination count={Math.ceil(filteredFruits.length / rowsPerPage)}
                page={page + 1} onChange={(_, v) => setPage(v - 1)} color="primary" />
            </Box>
          )}
        </Box>
      )}

      {/* Delete Dialog */}
      <ConfirmationDialog open={Boolean(deleteDialog)} title="Confirm Delete"
        message={`Are you sure you want to delete "${deleteDialog?.name}"? This cannot be undone.`}
        confirmText="Delete" cancelText="Cancel"
        onConfirm={confirmDelete} onCancel={() => setDeleteDialog(null)} />

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem onClick={() => { navigate(`/admin/fruits/edit/${selectedFruit?.id}`); handleMenuClose(); }}>
          <Edit sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem onClick={() => { handleToggleStatus(selectedFruit); handleMenuClose(); }}>
          {selectedFruit?.active
            ? <><Cancel sx={{ mr: 1 }} /> Deactivate</>
            : <><CheckCircle sx={{ mr: 1 }} /> Activate</>}
        </MenuItem>
        <MenuItem onClick={() => { setDeleteDialog(selectedFruit); handleMenuClose(); }}
          sx={{ color: 'error.main' }}>
          <Delete sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>
    </Container>
  );
};

export default AdminFruits;